/**
 * MiniSparkline
 * =============
 *
 * Shared tiny line-chart used by both the Compact card view and the
 * Rows table view of Data Signals. Strips Nivo down to the bare
 * minimum: monotone curve, faint area band, no axes / grid / points.
 *
 * Reads ``avgValue`` per bin (the same field used by the full
 * ``SignalBinMiniChart``) and falls back to a placeholder when the
 * bins are categorical or empty.
 */

import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { MAX_CHART_POINTS } from '../../containers/charts/signalBinsMini';

const MiniSparkline = ({
  bins,
  height = 36,
  width,
  color,
  areaOpacity = 0.18,
  lineWidth = 1.25,
}) => {
  const theme = useTheme();
  const stroke = color || theme.palette.primary.main;

  const data = useMemo(() => {
    if (!Array.isArray(bins) || bins.length === 0) return [];
    const windowed = bins.length > MAX_CHART_POINTS ? bins.slice(-MAX_CHART_POINTS) : bins;
    const points = windowed
      .map((b, i) => {
        const v = Number(b?.avgValue);
        if (!Number.isFinite(v)) return null;
        return { x: i, y: v };
      })
      .filter(Boolean);
    return points.length > 1 ? [{ id: 'sl', data: points }] : [];
  }, [bins]);

  if (data.length === 0) {
    return (
      <Box sx={{
        width: width || '100%',
        height,
        color: 'text.disabled',
        fontSize: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        —
      </Box>
    );
  }

  return (
    <Box sx={{ width: width || '100%', height }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
        curve="monotoneX"
        enablePoints={false}
        enableArea
        areaOpacity={areaOpacity}
        enableGridX={false}
        enableGridY={false}
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        colors={[stroke]}
        animate={false}
        isInteractive={false}
        lineWidth={lineWidth}
      />
    </Box>
  );
};

export default MiniSparkline;
